// src/App.jsx
import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  Outlet,
  useParams,
  useNavigate,
  useLocation,
} from "react-router-dom";
import { useEffect, lazy, Suspense } from "react";
import * as Sentry from "@sentry/react";
import {
  doc,
  getDoc,
  collection,
  query,
  where,
  limit,
  getDocs,
} from "firebase/firestore";
import { db } from "./config/firebase";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { TenantProvider, useTenant } from "./context/TenantContext";
import Navbar from "./components/layout/Navbar";
import Footer from "./components/layout/Footer";
import ProtectedRoute from "./components/layout/ProtectedRoute";
import AdminRoute from "./components/layout/AdminRoute";
import TenantBookingPage from "./components/home/TenantBookingPage";

// ── Code splitting ────────────────────────────────────────────────────────────
// Customer-facing booking pages stay STATIC imports (always instantly loaded —
// the revenue path is untouched). Heavy back-office and low-traffic pages are
// lazy: their code downloads only when that route is visited, which keeps the
// main bundle a customer downloads small.
import BookingPage from "./pages/BookingPage";
import LoginPage from "./pages/LoginPage";
import SignupPage from "./pages/SignupPage";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import ServicesPage from "./pages/ServicesPage";
import AboutPage from "./pages/AboutPage";
import GuestBookingPage from "./pages/GuestBookingPage";
import StaffRoute from "./components/layout/StaffRoute";
import SuperAdminRoute from "./components/layout/SuperAdminRoute";
import { LoadingSpinner, TenantPageSkeleton } from "./components/ui";

// After a deploy the old content-hashed chunk files disappear; a user who kept
// a tab open can then fail to load a lazy chunk. Reload once to pick up the
// fresh build (sessionStorage guards against a reload loop).
function lazyRetry(importFn) {
  return lazy(() =>
    importFn()
      .then((mod) => {
        sessionStorage.removeItem("bookyara_chunk_reload");
        return mod;
      })
      .catch((err) => {
        if (!sessionStorage.getItem("bookyara_chunk_reload")) {
          sessionStorage.setItem("bookyara_chunk_reload", "1");
          window.location.reload();
          return new Promise(() => {}); // page is reloading — never resolves
        }
        throw err; // second failure: real problem, surface to the boundary
      })
  );
}

const MarketingPage      = lazyRetry(() => import("./pages/MarketingPage"));
const OnboardingPage     = lazyRetry(() => import("./pages/OnboardingPage"));
const ProfilePage        = lazyRetry(() => import("./pages/ProfilePage"));
const AdminDashboardPage = lazyRetry(() => import("./pages/AdminDashboardPage"));
const StaffInvitePage    = lazyRetry(() => import("./pages/StaffInvitePage"));
const StaffDashboardPage = lazyRetry(() => import("./pages/StaffDashboardPage"));
const SuperAdminPage     = lazyRetry(() => import("./pages/SuperAdminPage"));
const PrivacyPage        = lazyRetry(() => import("./pages/PrivacyPage"));
const TermsPage          = lazyRetry(() => import("./pages/TermsPage"));

// Full-page fallback shown while a lazy chunk downloads (usually <300ms).
function PageLoader() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-surface-50">
      <LoadingSpinner />
    </div>
  );
}

// Contains a crash to the current page instead of whitescreening the app.
function RouteBoundary({ children }) {
  return (
    <Sentry.ErrorBoundary
      fallback={
        <div className="min-h-screen flex flex-col items-center justify-center bg-surface-50 px-4 text-center">
          <div className="text-4xl mb-4">😕</div>
          <h2 className="font-semibold text-2xl text-ink-900 mb-2">This page hit a snag</h2>
          <p className="text-ink-400 text-sm font-sans mb-6">
            The rest of the app is fine. Reload to try again.
          </p>
          <button onClick={() => window.location.reload()} className="btn-primary">
            Reload page
          </button>
        </div>
      }
    >
      {children}
    </Sentry.ErrorBoundary>
  );
}

// ── Tenant home (public booking page for a specific business) ─────────────────
function TenantHomePage() {
  return <TenantBookingPage />;
}

// ── Tenant shell: loads tenant from Firestore then renders children ───────────
function TenantShell() {
  const { tenantLoading, tenantError } = useTenant();

  if (tenantLoading) {
    return <TenantPageSkeleton />;
  }

  if (tenantError) {
    const isSuspended = tenantError === "suspended";
    return (
      <div className="flex flex-col min-h-screen items-center justify-center bg-surface-50 text-center px-4">
        <div className="text-5xl mb-4">{isSuspended ? "🚫" : "🔍"}</div>
        <h2 className="font-semibold text-2xl text-ink-900 mb-2">
          {isSuspended ? "This page is unavailable" : "Business not found"}
        </h2>
        <p className="text-ink-400 text-sm font-sans mb-6">
          {isSuspended
            ? "This business is temporarily unavailable. Please check back later."
            : "The booking page you're looking for doesn't exist or has been removed."}
        </p>
        <a href="/" className="btn-primary">
          ← Back to Bookyara
        </a>
      </div>
    );
  }

  return (
    <div className="flex flex-col min-h-screen bg-surface-50">
      <TenantShellContent />
    </div>
  );
}

function TenantShellContent() {
  const location = useLocation();
  const { slug } = useParams();
  // Hide Navbar/Footer on the tenant home — it has its own app-like chrome
  const isHome =
    location.pathname === `/${slug}` || location.pathname === `/${slug}/`;
  return (
    <>
      {!isHome && <Navbar />}
      <main className="flex-1">
        <Outlet />
      </main>
      {!isHome && <Footer />}
    </>
  );
}

// ── Root redirect: logged-in users go to their tenant, guests see marketing ───
function RootRedirect() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (loading) return;
    if (!user) return; // stay on marketing page

    async function redirect() {
      try {
        // 1. Check user profile for tenantId
        const profileSnap = await getDoc(doc(db, "users", user.uid));
        const profile = profileSnap.data();

        if (profile?.role === "admin" && profile?.tenantId) {
          const t = await getDoc(doc(db, "tenants", profile.tenantId));
          const slug = t.data()?.slug;
          if (slug) {
            navigate(`/${slug}/admin`, { replace: true });
            return;
          }
        }
        if (profile?.role === "staff") {
          navigate("/staff-dashboard", { replace: true });
          return;
        }
        if (profile?.tenantId) {
          const t = await getDoc(doc(db, "tenants", profile.tenantId));
          const slug = t.data()?.slug;
          if (slug) {
            navigate(`/${slug}`, { replace: true });
            return;
          }
        }

        // 2. Fallback: find tenant from bookings
        const snap = await getDocs(
          query(
            collection(db, "bookings"),
            where("userId", "==", user.uid),
            limit(1),
          ),
        );
        if (!snap.empty) {
          const tid = snap.docs[0].data().tenantId;
          const t = await getDoc(doc(db, "tenants", tid));
          const slug = t.data()?.slug;
          if (slug) {
            navigate(`/${slug}`, { replace: true });
            return;
          }
        }
      } catch {
        // stay on marketing page
      }
    }
    redirect();
  }, [user, loading, navigate]);

  return <MarketingPage />;
}

// ── Tenant layout: provides TenantContext for /:slug/* routes ─────────────────
function TenantLayout() {
  const { slug } = useParams();
  return (
    <TenantProvider slug={slug}>
      <TenantShell />
    </TenantProvider>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Suspense fallback={<PageLoader />}>
        <Routes>
          {/* ── Marketing & onboarding (no tenant context) ── */}
          <Route path="/" element={<RootRedirect />} />
          <Route path="/onboard" element={<RouteBoundary><OnboardingPage /></RouteBoundary>} />

          {/* ── Legal ── */}
          <Route path="/privacy" element={<PrivacyPage />} />
          <Route path="/terms" element={<TermsPage />} />

          {/* ── Global auth pages ── */}
          <Route path="/login" element={<LoginPage />} />
          <Route path="/signup" element={<SignupPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/staff-invite" element={<StaffInvitePage />} />
          <Route
            path="/staff-dashboard"
            element={
              <StaffRoute>
                <RouteBoundary><StaffDashboardPage /></RouteBoundary>
              </StaffRoute>
            }
          />
          <Route
            path="/superadmin"
            element={
              <SuperAdminRoute>
                <RouteBoundary><SuperAdminPage /></RouteBoundary>
              </SuperAdminRoute>
            }
          />

          {/* ── Tenant routes: all under /:slug ── */}
          <Route path="/:slug" element={<TenantLayout />}>
            <Route index element={<TenantHomePage />} />
            <Route path="services" element={<ServicesPage />} />
            <Route path="about" element={<AboutPage />} />

            <Route path="book" element={<BookingPage />} />
            <Route path="booking/:bookingId" element={<GuestBookingPage />} />
            <Route
              path="profile"
              element={
                <ProtectedRoute>
                  <ProfilePage />
                </ProtectedRoute>
              }
            />
            <Route
              path="admin"
              element={
                <AdminRoute>
                  <RouteBoundary><AdminDashboardPage /></RouteBoundary>
                </AdminRoute>
              }
            />
          </Route>

          {/* ── Catch-all ── */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        </Suspense>
      </AuthProvider>
    </BrowserRouter>
  );
}
